<template>
  <section class="course-date-cover grid">
    <div class="content">
      <div class="left-col">
        <header class="header">
          <p
            class="level t-p-s-130"
            v-html="nbsp(t(`course-level.${page.course.course_level}`))"
          />
          <h1 class="title t-h2" v-html="nbsp(page.course.title)" />
          <p class="program t-p-m-130" v-html="nbsp(page.course.name)" />
        </header>
        <BaseButton
          :tag="NuxtLink"
          :href="page.register_url"
          size="l"
          :text="t('course-date-cover.sign-up')"
        />
      </div>
      <div class="right-col">
        <header class="city-header">
          <p class="city t-h5" v-html="nbsp(page.city)" />
          <p class="date t-p-m-130" v-html="nbsp(date)" />
        </header>
        <section v-if="page.teachers.length" class="teachers">
          <DateCoverTeacher
            v-for="teacher in page.teachers"
            :key="teacher.id"
            :teacher="teacher"
          />
        </section>
        <p class="price-title t-h5">{{ t('course-date-cover.price') }}</p>
        <section class="prices">
          <p v-for="{ id, type, price } in prices" :key="id" class="price">
            <span class="price-type t-p-m-130" v-html="nbsp(type)" />
            <span class="amount t-p-m-130" v-html="nbsp(getPrice(price))" />
          </p>
        </section>
      </div>
    </div>
    <div class="image-container">
      <BaseImage class="fill-space" :image="page.image" />
    </div>
  </section>
</template>

<script setup lang="ts">
import entries from 'lodash/fp/entries'
import flow from 'lodash/fp/flow'
import map from 'lodash/fp/map'
import pick from 'lodash/fp/pick'
import pickBy from 'lodash/fp/pickBy'

import { NuxtLink } from '#components'
import type { PublicCourseAndEventDatePage } from '@/service/__generated-api'

const props = defineProps<{ page: PublicCourseAndEventDatePage }>()
const { t } = useI18n()

const date = computed(() =>
  getCourseDate(props.page.start_date, props.page.end_date)
)

const getPrice = (price: number) => {
  if (price === 0) return t('free')

  return `${price.toFixed(2).replace('.', ',')} ${props.page.currency}`
}

const prices = computed(() =>
  flow(
    pick<PublicCourseAndEventDatePage, keyof PublicCourseAndEventDatePage>([
      'course_fee',
      'student_fee',
      'repeater_fee',
      'senior_fee'
    ]),
    pickBy<number | undefined | null>(
      (price): price is number => typeof price === 'number'
    ),
    entries,
    map(([type, price]: [string, number]) => {
      const typeWithoutSuffix = type.replace('_fee', '')

      return {
        id: typeWithoutSuffix,
        type: t(`course-price.${typeWithoutSuffix}`),
        price
      }
    })
  )(props.page)
)
</script>

<style scoped lang="scss">
@mixin divider($gap: 19px) {
  padding-bottom: rem($gap);
  border-bottom: 1px solid $layout-divider;
}

.image-container {
  @include column(xs, all);
  position: relative;
  order: 1;
  aspect-ratio: 3 / 2;
  margin-inline: rem(18px);
  overflow: hidden;
  border-radius: $border-radius-mid $border-radius-mid 0 0;

  @include media-up(md) {
    margin-inline: rem(40px);
  }

  @include media-up(s-lg) {
    height: rem(432px);
    aspect-ratio: unset;
    margin-inline: rem(20px);
    border-radius: $border-radius-mid;
  }
}

.content {
  @include column(xs, 12);
  order: 2;
  padding: rem(36px) rem(20px) rem(42px);
  background-color: $layout-background-02;
  border-radius: 0 0 $border-radius-mid $border-radius-mid;
  box-shadow: $base-shadow;

  @include media-up(md) {
    padding-inline: rem(40px);
  }

  @include media-up(s-lg) {
    z-index: 1;

    display: flex;
    margin-top: rem(-60px);
    border-radius: $border-radius-mid;
  }

  @include media-up(lg) {
    padding-inline: rem(80px);
  }

  @include media-up(s-xl) {
    border-radius: $border-radius;
  }
}

.left-col {
  @include flex-col;
  gap: rem(32px);
  padding: 0 rem(40px) rem(32px) 0;

  @include media-up(s-lg) {
    width: 33.33%;
  }

  @include media-up(lg) {
    justify-content: space-between;
    width: 50%;
    padding-bottom: 0;
  }
}

.level {
  margin-bottom: rem(14px);
  color: $layout-text-03;
}

.title {
  margin-bottom: rem(10px);
}

.right-col {
  @include flex-col;
  gap: rem(20px);

  @include media-up(s-lg) {
    width: 66.66%;
    padding-left: rem(39px);
    border-left: 1px solid $layout-divider;
  }

  @include media-up(lg) {
    width: 50%;
  }
}

.city-header {
  @include divider;
  display: flex;
  gap: rem(16px);
  align-items: flex-end;
}

.teachers {
  @include divider;
  @include flex-col;
  gap: rem(10px);
}

.prices {
  @include flex-col;
  gap: rem(12px);

  > :not(:last-child) {
    @include divider(11px);
  }
}

.price {
  @include flex-col;

  @include media-up(md) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  }
}
</style>
